/**
 * Accessory data helpers — pure/leaf module (no DOM, no module-level window).
 * Extracted verbatim from custom-products.js to break the circular dependency
 * with recommended-accessories-unified.js. Imported by both custom-products.js
 * (via accessory-render) and recommended-accessories-unified.js.
 */

export function normalizeAccessories(input) {
    // Return an ARRAY, never a Set
    let arr;
    if (Array.isArray(input)) arr = input.slice();
    else if (typeof input === 'string') arr = input.split(',');
    else if (input && typeof input[Symbol.iterator] === 'function') arr = Array.from(input); // Set, etc.
    else arr = [];

    arr = arr.map(s => String(s).trim()).filter(Boolean);
    return Array.from(new Set(arr)); // de-dup as array
}

export function signatureFromAccessories(accessories) {
    // Accept array OR iterable and always return a STRING.
    // String check must precede the iterable check — strings ARE iterable,
    // and letting them hit Array.from() spreads them into characters.
    const arr = Array.isArray(accessories)
      ? accessories
      : (typeof accessories === 'string')
        ? accessories.split(',')
        : (accessories && typeof accessories[Symbol.iterator] === 'function' ? Array.from(accessories) : []);
    return arr.map(s => String(s).trim()).filter(Boolean).join('|');
}

export async function fetchAccessoryBySkuFlexible(sku, token, signal, log = console) {
    const EXTENDED_PRODUCTS_QUERY_PAGINATED = `query ExtendedProductsById($productIds: [Int!] $cursor: String) {site { products(entityIds: $productIds, after: $cursor) { edges { node { name path entityId sku variants(first: 25) { edges { node { entityId sku defaultImage { url(width: 320) } inventory { isInStock } productOptions(first: 10) { edges { node { entityId displayName ... on MultipleChoiceOption { displayStyle values(first: 18) { edges { node { entityId isDefault label ... on SwatchOptionValue { imageUrl(width: 30) } } } } } } } } prices { price { formatted value } retailPrice { value } salePrice { value } basePrice { value } } } } } } } pageInfo { hasNextPage endCursor } } } }`;

  // --- Normalize the sku to a single STRING ---
  let skuStr = sku;
  if (skuStr instanceof Set) skuStr = Array.from(skuStr)[0];
  if (Array.isArray(skuStr)) skuStr = skuStr[0];
  if (typeof skuStr !== 'string') skuStr = String(skuStr || '');

  skuStr = skuStr.trim();
  if (!skuStr) {
    log.warn('[recAcc] Empty SKU after normalization');
    return { sku: '', product: null, timingMs: 0 };
  }


    const body = {
    //   query: `
    //   query GetAccessoryBySku($sku: String!, $variantSkus: [String!]) {
    //     site {
    //       byProduct: product(sku: $sku) {
    //         name sku entityId path
    //         prices { price { value formattedV2 } }
    //         defaultImage { url(width: 320) }
    //         variants(skus: $variantSkus) {
    //           edges { node {
    //             sku entityId
    //             prices { price { value formattedV2 } }
    //             defaultImage { url(width: 320) }
    //           } }
    //         }
    //       }
    //     }
    //   }
    // `,
    //  variables: { sku, variantSkus: [sku] },
    query:`query GetAccessoryId {
              site {
                product (sku: "${sku}") {
                  prices {
                    price {
                      value
                      formattedV2
                    }
                  }
                  defaultImage {
                    url(width: 320)
                  }
                  name
                  sku
                  entityId
                  addToCartUrl
                  path
                  availabilityV2 {
                    status
                  }
                  variants(skus: ["${sku}"]) {
                    edges {
                      node {
                        prices {
                          price {
                            value
                            formattedV2
                          }
                        }
                        sku
                        defaultImage {
                          url(width: 320)
                        }
                        entityId
                        options(first: 3) {
                          edges {
                            node {
                              values(first: 2) {
                                edges {
                                  node {
                                    entityId
                                    label
                                  }
                                }
                              }
                              displayName
                              entityId
                            }
                          }
                        }
                      }
                    }
                  }
                  productOptions {
                    edges {
                      node {
                        ... on MultipleChoiceOption {
                          __typename
                          values(first: 18) {
                            edges {
                              node {
                                ... on SwatchOptionValue {
                                  imageUrl(width: 30)
                                  isSelected
                                }
                                entityId
                                isDefault
                                label
                              }
                            }
                          }
                          displayStyle
                        }
                      }
                    }
                  }
                }
              }
            }`
    };

    const t0 = performance.now();
    const res = await fetch('/graphql', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    });
    const dt = (performance.now() - t0).toFixed(1);

    let json;
    try { json = await res.json(); } catch (err) {
      log.error(`[recAcc] Parse JSON failed for ${sku}`, err);
      throw err;
    }
    log.debug(`[recAcc] Fetch ${sku} -> HTTP ${res.status} in ${dt}ms`);

    if (!res.ok) {
      log.error(`[recAcc] HTTP error for ${sku}`, { status: res.status, json });
      throw new Error(`GraphQL HTTP ${res.status}`);
    }
    if (json.errors) {
      log.warn(`[recAcc] GraphQL errors for ${sku}`, json.errors);
    }

    const product = json?.data?.site?.product || null;
    if (!product || !product.entityId || !product.variants) {
      log.warn(`[recAcc] No product found for SKU`, sku);
      return { sku, product: null, timingMs: Number(dt) };
    }
    return { sku, product, timingMs: Number(dt) };
}
